import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CircularProgress, Typography } from "@mui/joy";
import { Box } from "@mui/system";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { InstalledBaseEventListTDAs } from "../../graphql/queries/installedBaseEventListTDAs";
import {
    InstalledBaseEventAttributeInput,
    ManualEventType
} from "../../codegen/graphql";
import TDAStepperForm from "../TDAStepperForm";
import { separateInstalledBaseEventAttributes } from "../../utils/separateInstalledBaseEventAttributes";
import {
    decorateMaintenanceOtherAttributes,
    getMaintenanceOtherSeedValues,
    getMaintenanceOtherTriggerValues,
    MAINTENANCE_OTHER_PAIRS,
    nullifyInactiveMaintenanceOtherValues,
} from "../../utils/maintenanceOtherAttributes";
import { useEventTypeLabels } from "../../utils/eventTypeLabels";
import { getEventTypeIcon } from "../../utils/eventTypeIcons";
import { normalizeEventFilesValue } from "../../utils/eventFilesValue";
import AssetNameplate from "../AssetNameplate";
import FilesDropzone from "./FilesDropzone";
import VacuumCheckFileInstructions from "./VacuumCheckFileInstructions";
import ReplacementAssetPicker from "./ReplacementAssetPicker";
import InstalledBaseNodePathBreadcrumb from "../InstalledBaseNodePathBreadcrumb";
import { FormikHelpersRef } from "../../types";
import { hasAttributeIndex, REMOVAL_DATE_INDEX, RUNNING_TIME_INDEX } from "../../utils/moveWizardSync";
import {
    EventDates,
    evaluateEventDatesChange,
    getDefaultEventDates,
    INSTALLATION_DATE_INDEX,
} from "../../utils/eventRunningTimeSync";

const EVENT_TYPE_FIELD = '_eventType';
const EVENT_TYPE_CATEGORY = '_eventTypeCategory';
const EVENT_TYPE_INPUT_TYPE = 'eventTypeTilePicker';

const FILES_FIELD = '_files';
const FILES_CATEGORY = 'eventFiles';
const FILES_INPUT_TYPE = 'eventFilesDropzone';

// Synthetic step injected only when the install-event flow starts without a
// known parent (Header "Register Asset"). The user picks the target node here
// and its value flows through `pickerNode` → `targetedNode` on submit.
const TARGET_NODE_FIELD = 'targetNodeId';
const TARGET_NODE_CATEGORY = '_targetNode';

// Expected wizard length for the install-event flow before the picked node's
// TDAs have loaded (the real step count is only known after the location is
// chosen). Used to drive the progress bar's "Step X of Y" placeholder total.
const INSTALL_EXPECTED_STEPS = 4;

// ASSET_EXCHANGE: synthetic step between the removal/exchange TDAs and the
// `newAsset`-tagged TDAs. The user picks (or creates) the replacement asset
// here; its id flows through `event.replacementAssetId` on submit. Category
// name intentionally omits the leading `_` so deriveStepKeys keeps it in the
// natural attribute-array position rather than hoisting it to the front.
const REPLACEMENT_ASSET_FIELD = '_replacementAsset';
const REPLACEMENT_ASSET_CATEGORY = 'replacementAsset';
const REPLACEMENT_ASSET_INPUT_TYPE = 'replacementAssetPicker';
const NEW_ASSET_CATEGORY = 'newAsset';

export interface InstalledBaseEventFormVariables {
    assetId?: string;
    eventType: ManualEventType;
    /** Target node where the event is recorded — typically the asset's own installedBaseNode id.
     *  May be undefined when the form is in install mode and the user hasn't picked a node yet. */
    targetedNode?: string;
    /** New parent for relocating events (ASSET_REMOVE, ASSET_EXCHANGE). Optional otherwise. */
    relocationNode?: string;
    /** Replacement asset id (ASSET_EXCHANGE only). The id of an existing asset
     *  the user picked or a newly created asset's id. */
    replacementAssetId?: string;
    attributes: InstalledBaseEventAttributeInput[];
    /** Files the user dropped in the final wizard step. Empty array when none. */
    files: File[];
    /** ASSET_INSTALL only: index into `files` of the image to set as the asset's
     *  node image, or null/undefined when none was marked. */
    assetImageIndex?: number | null;
}

interface Props {
    /**
     * Asset the event is created for. Optional — flows that create the asset at submit time
     * (e.g. register asset) can omit this and stitch the id in via `onSubmit`.
     */
    assetId?: string;
    /**
     * The installed-base-node this event is recorded against. Used as both
     * the `id` argument of the TDA query and the `event.targetNodeId`
     * argument of the createInstalledBaseEvent mutation. May be null/undefined
     * for install flows that haven't picked a target yet — the form then sources
     * the targetedNode from the first picker attribute returned by the TDA query.
     */
    targetedNode?: string | null;
    /**
     * Forces the install-target location picker for ASSET_INSTALL even though a
     * `targetedNode` was passed. Set when the passed node is the asset's own
     * (ASSET-typed) node rather than a concrete install location — the user must
     * pick where to install. For non-install event types this has no effect.
     */
    forceInstallTargetPicker?: boolean;
    /** Initial event type. When `selectableEventTypes` is omitted, this is the only allowed value. */
    eventType: ManualEventType;
    /**
     * If provided, a synthetic event-type step is prepended to the wizard. Choosing a different
     * value refetches the TDAs; the user can navigate back to the event-type step at any time.
     * Should include `eventType` as one of the entries.
     */
    selectableEventTypes?: ManualEventType[];
    /**
     * Called with the prepared mutation variables when the form is submitted. The caller is
     * responsible for firing `createInstalledBaseEvent` (or bundling it with other work, e.g.
     * a deferred `createAsset` mutation in the register-asset flow).
     */
    onSubmit: (variables: InstalledBaseEventFormVariables) => Promise<void> | void;
    initialFormValues?: Record<string, any>;
    finishButtonText?: string;
    onCancel?: () => void;
    isSubmitting?: boolean;
    hideStepper?: boolean;
    /**
     * When provided, a compact AssetNameplate is rendered above the form body in place of
     * the default WizardStepper, giving the user persistent context about the asset.
     */
    assetLocationId?: string;
    /**
     * Fires when the user has settled on an event type so the parent (e.g. modal title)
     * can react. When `selectableEventTypes` is set, this stays `undefined` while the user
     * is still on the tile-picker step and only updates after they pick. When omitted, it
     * fires immediately on mount with the locked type.
     */
    onEventTypeChange?: (type: ManualEventType | undefined) => void;
    /**
     * Client root (pathNodes[0]) of the targeted node. Scopes the relocation picker
     * (ASSET_REMOVE / ASSET_EXCHANGE) to the asset's own client tree — the backend
     * rejects cross-client relocation. The install-target picker stays unscoped:
     * installing at another client is how an asset changes clients.
     */
    relocationRootNodeId?: string;
    /**
     * The asset location's resolved hours per day. Re-derives runningTimeHours when
     * the user changes installationDate or removalDate (see eventRunningTimeSync.ts);
     * without it the backend default stays untouched.
     */
    averageRuntimePerDay?: number | null;
}

interface EventTypeTilePickerRendererProps {
    attribute: {
        index: string;
        optionValues?: Array<{ name: string; value: string }>;
    };
    value: string | null | undefined;
    setFieldValue: (field: string, value: any) => void;
}

const EventTypeTilePicker = ({ attribute, value, setFieldValue }: EventTypeTilePickerRendererProps) => {
    const options = attribute.optionValues ?? [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {options.map((opt) => {
                const eventType = opt.value as ManualEventType;
                const selected = value === opt.value;
                return (
                    <Box
                        key={opt.value}
                        onClick={() => setFieldValue(attribute.index, opt.value)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '13px',
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: selected ? 'primary.main' : '#ccc',
                            borderRadius: '5px',
                            backgroundColor: selected ? '#e0e0e0' : 'transparent',
                            transition: 'border-color 0.15s, background-color 0.15s',
                            '&:hover': {
                                borderColor: selected ? 'primary.main' : '#999',
                                backgroundColor: selected ? '#e0e0e0' : 'rgba(0, 0, 0, 0.02)',
                            },
                        }}
                    >
                        {getEventTypeIcon(eventType)}
                        <Typography level="title-lg" sx={{ color: '#333' }}>
                            {opt.name}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

const InstalledBaseEventForm = ({
    assetId,
    targetedNode,
    forceInstallTargetPicker,
    eventType: initialEventType,
    selectableEventTypes,
    onSubmit,
    initialFormValues,
    finishButtonText,
    onCancel,
    isSubmitting,
    hideStepper,
    assetLocationId,
    onEventTypeChange,
    relocationRootNodeId,
    averageRuntimePerDay,
}: Props) => {
    const { t } = useTranslation();
    const [currentEventType, setCurrentEventType] = useState<ManualEventType>(initialEventType);
    const [hasUserPickedEventType, setHasUserPickedEventType] = useState(false);
    // Install-target-pick flows only: the node currently selected in the picker
    // step (drives the node-scoped TDA prefetch) and the node the user has
    // confirmed by advancing past the picker (whose TDAs populate the wizard and
    // which keys the stepper remount). `advancing` shows a Next-button spinner
    // while that node's TDAs load.
    const [pickedParentNode, setPickedParentNode] = useState<string>();
    const [confirmedNode, setConfirmedNode] = useState<string>();
    const [advancing, setAdvancing] = useState(false);
    // ASSET_MAINTENANCE only: live values of the selects that toggle their paired
    // *Other free-text fields (see maintenanceOtherAttributes.ts).
    const [maintenanceTriggerValues, setMaintenanceTriggerValues] = useState<Record<string, unknown>>({});
    const eventTypeLabels = useEventTypeLabels();

    useEffect(() => {
        // Only surface the event type once it's actually settled: locked (no picker step)
        // or after the user has picked one. Otherwise tell the parent there's no selection
        // yet so it can hide the icon / fall back to a neutral title.
        if (!selectableEventTypes || hasUserPickedEventType) {
            onEventTypeChange?.(currentEventType);
        } else {
            onEventTypeChange?.(undefined);
        }
    }, [currentEventType, hasUserPickedEventType, selectableEventTypes, onEventTypeChange]);

    // The install target is chosen via the location picker whenever the current
    // event is ASSET_INSTALL and the launch context isn't a concrete location:
    // no node at all (header/register flow), or the asset's own ASSET-typed node
    // (`forceInstallTargetPicker`, e.g. asset dashboard / more button / event
    // history). When false, the passed `targetedNode` is used directly.
    const installTargetPickActive =
        currentEventType === ManualEventType.ASSET_INSTALL && (!targetedNode || !!forceInstallTargetPicker);

    // The TDAs are always loaded scoped to a parent node: when picking the
    // install target, that's the node the user selects (prefetched via
    // `pickedParentNode`, built into steps only once `confirmedNode` is set so an
    // early prefetch can't grow the steps); otherwise the caller's `targetedNode`.
    const queryNode = installTargetPickActive ? pickedParentNode : targetedNode;
    const nodeScoped = useQuery(InstalledBaseEventListTDAs, {
        variables: {
            id: queryNode!,
            typeDefinitionIndex: "INSTALLED_BASE_EVENT_CREATE",
            options: {
                eventType: currentEventType,
                parentInstalledBaseNodeId: queryNode,
                ...(assetId ? { assetId } : {}),
            },
        },
        fetchPolicy: "network-only",
        skip: !queryNode,
    });

    const loading = nodeScoped.loading;
    // Id of the node the currently-loaded TDA payload belongs to — used to tell a
    // fresh/stale `nodeScoped.data` apart from the node we're acting on.
    const nodeScopedId = _.get(nodeScoped.data, 'installedBaseNode.id') as string | undefined;

    const tdaSourceNode = installTargetPickActive ? confirmedNode : targetedNode;
    const fetchedAttributes = tdaSourceNode
        ? (_.get(
            nodeScoped.data ?? nodeScoped.previousData,
            'installedBaseNode.installedBaseEventList.typeDefinitionAttributeList.typeDefinitionAttributes',
            [],
        ) as any[])
        : [];

    // Date → runningTime sync (see eventRunningTimeSync.ts), active whenever the
    // loaded TDAs carry both dates and the running time (ASSET_REMOVE). Kept in
    // refs, not form values: separateInstalledBaseEventAttributes submits every
    // non-`_` key. Re-seeded from the defaults whenever a new TDA payload loads.
    const runningTimeSyncEnabled =
        hasAttributeIndex(fetchedAttributes, INSTALLATION_DATE_INDEX) &&
        hasAttributeIndex(fetchedAttributes, REMOVAL_DATE_INDEX) &&
        hasAttributeIndex(fetchedAttributes, RUNNING_TIME_INDEX);
    const runningTimeSyncRef = useRef<{
        source: any[];
        dates: EventDates;
        // Recomputed runningTime whose field isn't on the mounted step yet.
        pending: number | null;
    } | null>(null);
    const stepFormikRef = useRef<FormikHelpersRef | null>(null);

    const getRunningTimeSync = () => {
        if (runningTimeSyncRef.current?.source !== fetchedAttributes) {
            runningTimeSyncRef.current = {
                source: fetchedAttributes,
                dates: getDefaultEventDates(fetchedAttributes),
                pending: null,
            };
        }
        return runningTimeSyncRef.current;
    };

    const handleFormikHelpersRef = (ref: FormikHelpersRef) => {
        stepFormikRef.current = ref;
        // Flush a parked runningTime as soon as the step carrying the field mounts.
        // Done here, not in the values echo: TDAForm reports a new step's values
        // before its helpers, so the echo would still write into the previous step.
        if (!runningTimeSyncEnabled) return;
        const sync = getRunningTimeSync();
        if (sync.pending !== null && RUNNING_TIME_INDEX in ref.getValues()) {
            ref.setFieldValue(RUNNING_TIME_INDEX, sync.pending);
            sync.pending = null;
        }
    };

    const syncRunningTime = (values: Record<string, any>) => {
        const sync = getRunningTimeSync();
        const { dates, nextRunningTime } = evaluateEventDatesChange(sync.dates, values, averageRuntimePerDay);
        sync.dates = dates;
        if (nextRunningTime === undefined) return;
        // The date wins, even over a manually edited runningTime.
        if (RUNNING_TIME_INDEX in values && stepFormikRef.current) {
            stepFormikRef.current.setFieldValue(RUNNING_TIME_INDEX, nextRunningTime);
            sync.pending = null;
        } else {
            sync.pending = nextRunningTime;
        }
    };

    const eventTypeAttribute = useMemo(() => {
        if (!selectableEventTypes || selectableEventTypes.length === 0) return null;
        return {
            index: EVENT_TYPE_FIELD,
            name: t('Event Type'),
            type: 'select',
            inputType: EVENT_TYPE_INPUT_TYPE,
            disabled: false,
            value: null,
            categories: [EVENT_TYPE_CATEGORY],
            dynamicFilterListKey: null,
            list: false,
            hint: null,
            dataLoaderConfig: null,
            parentAttributeIndex: null,
            optionValues: selectableEventTypes.map((type) => ({
                name: eventTypeLabels[type] ?? type,
                value: type,
                translatable: false,
                dependsOnParentValues: null,
            })),
            rules: [{ name: 'required', message: t('Event type is required') }],
            addOnAttribute: null,
            subAttributes: null,
        };
    }, [selectableEventTypes, eventTypeLabels, t]);

    // ASSET_VACUUM_CHECK: the completed report (filled-in template) is mandatory.
    const isVacuumCheck = currentEventType === ManualEventType.ASSET_VACUUM_CHECK;

    const filesAttribute = useMemo(() => ({
        index: FILES_FIELD,
        name: t('Files'),
        type: 'file',
        inputType: FILES_INPUT_TYPE,
        disabled: false,
        value: null,
        categories: [FILES_CATEGORY],
        dynamicFilterListKey: null,
        list: false,
        hint: null,
        dataLoaderConfig: null,
        parentAttributeIndex: null,
        optionValues: null,
        rules: isVacuumCheck
            ? [{ name: 'required', message: t('Please upload the completed vacuum check form.') }]
            : [],
        addOnAttribute: null,
        subAttributes: null,
    }), [isVacuumCheck, t]);

    const targetNodeAttribute = useMemo(() => {
        if (!installTargetPickActive) return null;
        return {
            index: TARGET_NODE_FIELD,
            name: t('New Parent Installed Base Location'),
            type: 'id',
            inputType: 'installedBaseNodeBrowser',
            disabled: false,
            value: null,
            categories: [TARGET_NODE_CATEGORY],
            dynamicFilterListKey: null,
            list: false,
            hint: t('Select the node in the installed base where the asset will be installed at.'),
            dataLoaderConfig: null,
            parentAttributeIndex: null,
            optionValues: null,
            rules: [{ name: 'required', message: t('Installation target is required') }],
            addOnAttribute: null,
            subAttributes: null,
            // Install destinations must not lie in the trash — restoring works via move.
            _includeTrash: false,
        };
    }, [installTargetPickActive, t]);

    const isExchange = currentEventType === ManualEventType.ASSET_EXCHANGE;
    const isMaintenance = currentEventType === ManualEventType.ASSET_MAINTENANCE;

    // Bridge: when the user is on the replacementAsset step and the embedded create-asset
    // form is mounted (i.e. no asset picked yet), the form registers its submit trigger
    // here so the outer wizard's Next button click can fire it instead of showing a
    // separate inner Next.
    const replacementSubmitRef = useRef<(() => Promise<void>) | null>(null);

    const handleBeforeNext = useCallback(async (stepKey: string) => {
        if (stepKey === REPLACEMENT_ASSET_CATEGORY && replacementSubmitRef.current) {
            await replacementSubmitRef.current();
            return;
        }
        // Leaving the install-location picker confirms the node and loads its
        // TDAs. Until then the wizard only has the picker (+ event-type / files);
        // once confirmed, the `confirmedNode` key remounts the stepper onto the
        // first node-scoped TDA step.
        if (installTargetPickActive && stepKey === TARGET_NODE_CATEGORY) {
            // No selection yet — let the step's required-field validation block.
            if (!pickedParentNode) return;
            // Already confirmed this exact node and its TDAs are loaded — let the
            // current (key=node) instance advance normally to the first TDA step
            // instead of re-confirming (which would throw and strand the user).
            if (confirmedNode === pickedParentNode && nodeScopedId === pickedParentNode) return;
            setAdvancing(true);
            try {
                if (nodeScopedId !== pickedParentNode) {
                    await nodeScoped.refetch();
                }
                setConfirmedNode(pickedParentNode);
            } finally {
                setAdvancing(false);
            }
            // Abort this instance's own advance — the key change remounts the
            // stepper directly onto the first TDA step.
            throw new Error('REMOUNT_ON_CONFIRM');
        }
    }, [installTargetPickActive, pickedParentNode, confirmedNode, nodeScopedId, nodeScoped]);

    const replacementAssetAttribute = useMemo(() => {
        if (!isExchange) return null;
        return {
            index: REPLACEMENT_ASSET_FIELD,
            name: t('Replacement asset'),
            type: 'id',
            inputType: REPLACEMENT_ASSET_INPUT_TYPE,
            disabled: false,
            value: null,
            categories: [REPLACEMENT_ASSET_CATEGORY],
            dynamicFilterListKey: null,
            list: false,
            hint: null,
            dataLoaderConfig: null,
            parentAttributeIndex: null,
            optionValues: null,
            rules: [{ name: 'required', message: t('Replacement asset is required') }],
            addOnAttribute: null,
            subAttributes: null,
        };
    }, [isExchange, t]);

    const attributes = useMemo(() => {
        let fetchedOrdered: any[] = fetchedAttributes;
        if (replacementAssetAttribute) {
            const newAssetAttrs = fetchedAttributes.filter((a) => (a.categories ?? []).includes(NEW_ASSET_CATEGORY));
            const mainAttrs = fetchedAttributes.filter((a) => !(a.categories ?? []).includes(NEW_ASSET_CATEGORY));
            fetchedOrdered = [...mainAttrs, replacementAssetAttribute, ...newAssetAttrs];
        }
        // Order matters for step derivation: event-type step first (when the user
        // chooses the type in-form), then the install-location picker, then the
        // node's TDAs, then files.
        return [
            ...(eventTypeAttribute ? [eventTypeAttribute] : []),
            ...(targetNodeAttribute ? [targetNodeAttribute] : []),
            ...fetchedOrdered,
            filesAttribute,
        ];
    }, [targetNodeAttribute, eventTypeAttribute, replacementAssetAttribute, fetchedAttributes, filesAttribute]);

    // ASSET_MAINTENANCE: hide each *Other free-text attribute while its paired select
    // isn't set to OTHER, and mark it required while visible.
    const decoratedAttributes = useMemo(
        () => (isMaintenance ? decorateMaintenanceOtherAttributes(attributes, maintenanceTriggerValues, t) : attributes),
        [attributes, isMaintenance, maintenanceTriggerValues, t]
    );

    const categoryLabels = useMemo(() => {
        const labels: Record<string, string> = { [FILES_CATEGORY]: t('Files') };
        if (eventTypeAttribute) {
            labels[EVENT_TYPE_CATEGORY] = t('Event Type');
        }
        if (targetNodeAttribute) {
            labels[TARGET_NODE_CATEGORY] = t('Installation Target');
        }
        if (replacementAssetAttribute) {
            labels[REPLACEMENT_ASSET_CATEGORY] = t('Replacement asset');
            labels[NEW_ASSET_CATEGORY] = t('Replacement details');
        }
        return labels;
    }, [eventTypeAttribute, targetNodeAttribute, replacementAssetAttribute, t]);

    const mergedInitialFormValues = useMemo(() => {
        const base = { ...(initialFormValues ?? {}) };
        if (eventTypeAttribute) {
            base[EVENT_TYPE_FIELD] = currentEventType;
        }
        // Keep the picked location visible after the stepper remounts on confirm.
        if (pickedParentNode) {
            base[TARGET_NODE_FIELD] = pickedParentNode;
        }
        return base;
    }, [initialFormValues, eventTypeAttribute, currentEventType, pickedParentNode]);

    // The *Other keys must stay in every step's Formik initial values even while
    // their field is filtered out — a changed key set would reset the step's form.
    // Passed as live-merged stepSeedValues (not initialFormValues) so they also reach
    // steps whose TDAs only arrive after mount (e.g. after an event-type switch).
    const maintenanceSeedValues = useMemo(
        () => (isMaintenance ? getMaintenanceOtherSeedValues(attributes) : undefined),
        [isMaintenance, attributes]
    );

    const customInputRenderers = useMemo(() => {
        // Rendered as JSX (TDAForm invokes renderers as plain functions, so the
        // component's hooks need their own component instance). Only the install
        // flow offers "use as asset image" on the dropped images.
        const allowAssetImage = currentEventType === ManualEventType.ASSET_INSTALL;
        const renderers: Record<string, any> = {
            [FILES_INPUT_TYPE]: (props: any) => (
                <FilesDropzone
                    {...props}
                    allowAssetImage={allowAssetImage}
                    {...(isVacuumCheck && {
                        instructions: <VacuumCheckFileInstructions />,
                        dropzoneText: t('Drag and drop the completed vacuum check form here, or click to upload it.'),
                    })}
                />
            ),
        };
        if (eventTypeAttribute) {
            renderers[EVENT_TYPE_INPUT_TYPE] = EventTypeTilePicker;
        }
        if (replacementAssetAttribute) {
            renderers[REPLACEMENT_ASSET_INPUT_TYPE] = (props: any) => (
                <ReplacementAssetPicker {...props} sourceAssetId={assetId} submitTriggerRef={replacementSubmitRef} />
            );
        }
        return renderers;
    }, [eventTypeAttribute, replacementAssetAttribute, assetId, currentEventType, isVacuumCheck, t]);

    const handleStepValuesChange = (_categoryKey: string, values: Record<string, any>) => {
        if (runningTimeSyncEnabled) {
            syncRunningTime(values);
        }
        // Capture the picked install location to prefetch its TDAs. Guarded on a
        // truthy value so steps that don't carry the field (it is seeded into
        // every step's initial values) can't clear the selection.
        if (installTargetPickActive) {
            const node = values[TARGET_NODE_FIELD];
            if (node && node !== pickedParentNode) {
                setPickedParentNode(node as string);
            }
        }
        if (isMaintenance) {
            // Only steps that actually carry the trigger selects report them (other
            // steps just carry the seeded *Other keys), so this can't reset a trigger.
            const triggers = getMaintenanceOtherTriggerValues(values);
            if (Object.keys(triggers).length > 0) {
                setMaintenanceTriggerValues((prev) => {
                    const next = { ...prev, ...triggers };
                    return _.isEqual(prev, next) ? prev : next;
                });
            }
        }
        if (!selectableEventTypes) return;
        const next = values[EVENT_TYPE_FIELD];
        if (next && next !== currentEventType) {
            setCurrentEventType(next as ManualEventType);
            setHasUserPickedEventType(true);
            // Switching event type invalidates any install-location pick: drop it
            // so the picker (and its node-scoped TDAs) rebuild for the new type.
            setPickedParentNode(undefined);
            setConfirmedNode(undefined);
            setMaintenanceTriggerValues({});
        }
    };

    const handleSubmit = async (submittedValues: any) => {
        // A runningTime recomputed after its step was last shown (e.g. a date changed,
        // then the user jumped past that step via the stepper) still has to land.
        const pendingRunningTime = runningTimeSyncEnabled ? getRunningTimeSync().pending : null;
        const formValues = pendingRunningTime !== null
            ? { ...submittedValues, [RUNNING_TIME_INDEX]: pendingRunningTime }
            : submittedValues;
        // Maintenance: clear *Other texts whose trigger select was moved away from
        // OTHER again — the form state keeps them even though the field is hidden.
        // Other event types: drop the keys entirely in case a maintenance step's
        // cached values leaked in via a shared category after an event-type switch.
        const cleanedValues = isMaintenance
            ? nullifyInactiveMaintenanceOtherValues(formValues)
            : _.omit(formValues, Object.values(MAINTENANCE_OTHER_PAIRS));
        const { attributes: eventAttributes, pickerNode } = separateInstalledBaseEventAttributes(cleanedValues, attributes);
        const filesValue = normalizeEventFilesValue(formValues[FILES_FIELD]);
        const files = filesValue.files;
        // When picking the install target, the location field IS the install
        // target (no relocation). Otherwise: with a passed targetedNode the picker
        // is a relocation target (ASSET_REMOVE / ASSET_EXCHANGE); without one the
        // picker sources the target.
        let effectiveTargetedNode: string | undefined;
        let effectiveRelocationNode: string | undefined;
        if (installTargetPickActive) {
            effectiveTargetedNode = formValues[TARGET_NODE_FIELD] ?? pickerNode;
            effectiveRelocationNode = undefined;
        } else {
            effectiveTargetedNode = targetedNode ?? pickerNode;
            effectiveRelocationNode = targetedNode ? pickerNode : undefined;
        }
        await onSubmit({
            assetId,
            eventType: currentEventType,
            targetedNode: effectiveTargetedNode ?? undefined,
            relocationNode: effectiveRelocationNode,
            replacementAssetId: formValues[REPLACEMENT_ASSET_FIELD] || undefined,
            attributes: eventAttributes,
            files,
            assetImageIndex: currentEventType === ManualEventType.ASSET_INSTALL ? filesValue.assetImageIndex : null,
        });
    };

    // Full-screen spinner only for the initial node-scoped load of flows that
    // resolve their TDAs from a known node. Install-target-pick flows always have
    // the picker step to render, so they keep their wizard chrome and show the
    // load on the Next button instead (`advancing`).
    if (loading && fetchedAttributes.length === 0 && !targetNodeAttribute) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Render the compact asset nameplate when we have either an assetLocation (existing
    // events fired from an asset/node menu) or just an assetId (install-from-register flow,
    // before the asset has a location). Either way, this replaces the wizard stepper at the
    // top so the user sees the asset they're working with and the first form step directly.
    // Target location breadcrumb for the install flow: the passed node when the
    // location is known up front (installed-base flow), or the node the user
    // confirmed in the picker (header / asset-dashboard flow), once confirmed.
    const breadcrumbNodeId =
        currentEventType === ManualEventType.ASSET_INSTALL
            ? (installTargetPickActive ? confirmedNode : (targetedNode ?? undefined))
            : undefined;

    const nameplateRef = assetLocationId || assetId;
    const headerContent = (nameplateRef || breadcrumbNodeId) ? (
        <Box sx={{display: 'flex', flexDirection: 'column'}}>
            {breadcrumbNodeId && (
                <Box sx={{mt: 0.5, mb: 1}}>
                    <InstalledBaseNodePathBreadcrumb nodeId={breadcrumbNodeId}/>
                </Box>
            )}
            {nameplateRef && (
                <Box
                    sx={{
                        mt: 1,
                        mb: 1,
                        p: 2,
                        border: '1px solid #ccc',
                        borderRadius: '5px',
                    }}
                >
                    <AssetNameplate
                        assetLocationId={assetLocationId}
                        assetId={assetLocationId ? undefined : assetId}
                        compact
                        disableNavigation
                        showLocation
                    />
                </Box>
            )}
        </Box>
    ) : undefined;

    // The install-location picker is the first step (index 0), unless an in-form
    // event-type step precedes it (index 1). After confirming the node, remount
    // straight onto the first node-scoped TDA step (just past the picker).
    const installPickerIndex = eventTypeAttribute ? 1 : 0;

    return (
        <TDAStepperForm
            // Remount onto the node's TDA steps once the install location is
            // confirmed; re-picking a different node remounts again, clearing any
            // stale step state.
            key={confirmedNode ?? 'pick'}
            initialActiveStep={confirmedNode ? installPickerIndex + 1 : 0}
            expectedTotalSteps={installTargetPickActive ? installPickerIndex + INSTALL_EXPECTED_STEPS : undefined}
            stepsFinalized={!installTargetPickActive || (!!confirmedNode && nodeScopedId === confirmedNode && !nodeScoped.loading)}
            isNextLoading={advancing}
            attributes={decoratedAttributes}
            initialFormValues={mergedInitialFormValues}
            stepSeedValues={maintenanceSeedValues}
            onSubmit={handleSubmit}
            onCancel={onCancel}
            finishButtonText={finishButtonText}
            isSubmitting={isSubmitting}
            onStepValuesChange={handleStepValuesChange}
            categoryLabels={categoryLabels}
            hideStepper={hideStepper}
            headerContent={headerContent}
            customInputRenderers={customInputRenderers}
            onBeforeNext={handleBeforeNext}
            nodePickerRootNodeId={installTargetPickActive ? undefined : relocationRootNodeId}
            onFormikHelpersRef={handleFormikHelpersRef}
        />
    );
};

export default InstalledBaseEventForm;
